/**
 * Organization (Structure / Supply Chain) results selectors.
 * Pure functions shared by ScopeFootprint and the HTML report so both show the same numbers.
 * Every mass returned here is in kg CO₂e (or kg of pollutant).
 */
import { findEmissionOptionMatches } from "../../components/page-context/Helpers/buildOptions";
import { scope1Categories, scope2Categories, scope3Categories } from "../../pages/Organization/ScopeCategory";
import { KG_PER_TONNE, shareOf, toFiniteNumber } from "./formatters";

export const SCOPES = {
  scope1: "Scope 1",
  scope2: "Scope 2",
  scope3: "Scope 3",
  avoided: "Avoided",
  biogenic: "Biogenic",
  uncategorized: "Uncategorized",
};

export const SCOPE_ORDER = [SCOPES.scope1, SCOPES.scope2, SCOPES.scope3, SCOPES.avoided, SCOPES.biogenic, SCOPES.uncategorized];
export const GROSS_SCOPES = [SCOPES.scope1, SCOPES.scope2, SCOPES.scope3];

export const POLLUTANTS = [
  { key: "co2", label: "CO₂" },
  { key: "ch4", label: "CH₄" },
  { key: "n2o", label: "N₂O" },
  { key: "nox", label: "NOx" },
  { key: "pm25", label: "PM2.5" },
  { key: "pm10", label: "PM10" },
  { key: "black_carbon", label: "Black Carbon" },
];
export const POLLUTANT_KEYS = POLLUTANTS.map((p) => p.key);

/** Metric key for total GHG; pollutant metrics use the POLLUTANTS keys. */
export const CO2E_METRIC = "co2e";

const normalizeText = (value) => String(value ?? "").toLowerCase().trim();
const isFlagSet = (value) => value === true || value === 1 || value === "1";

const categoryIn = (categories, category) => {
  const target = normalizeText(category);
  return categories.some((c) => normalizeText(c) === target);
};

/**
 * Detail rows written in t CO₂e instead of kg CO₂e:
 * - document upload extraction (OrganizationDocumentUpload) stores unit "tCO2e"
 * - evidence bill analysis (/evidence_analysis/save-batch-emissions) stores tCO2e totals
 *   with calculation_method "Supplier Specific" and type "Activity Data"
 */
export const TONNE_BASED_DETAIL_MARKERS = [
  (detail) => normalizeText(detail.unit) === "tco2e",
  (detail) => detail.calculation_method === "Supplier Specific" && detail.type === "Activity Data",
];

export const isTonneBasedDetail = (detail) => TONNE_BASED_DETAIL_MARKERS.some((matches) => matches(detail));

export const groupDetailsByEmission = (details = []) => {
  const map = new Map();
  (details || []).forEach((d) => {
    if (!d) return;
    if (!map.has(d.emission_id)) map.set(d.emission_id, []);
    map.get(d.emission_id).push(d);
  });
  return map;
};

/** An emission is tonne-based when all of its detail rows are. */
export const isTonneBasedEmission = (emission, detailsByEmission) => {
  const details = detailsByEmission.get(emission.id) || [];
  return details.length > 0 && details.every(isTonneBasedDetail);
};

export const getRawEmissionGwp = (emission) =>
  toFiniteNumber(emission.gwp) || toFiniteNumber(emission.gwp_total) || toFiniteNumber(emission.biogenic_gwp);

export const getCategoryScope = (category) => {
  if (categoryIn(scope1Categories, category)) return SCOPES.scope1;
  if (categoryIn(scope2Categories, category)) return SCOPES.scope2;
  if (categoryIn(scope3Categories, category)) return SCOPES.scope3;
  return SCOPES.uncategorized;
};

export const getEmissionScope = (emission) => {
  if (isFlagSet(emission.is_avoided)) return SCOPES.avoided;
  if (isFlagSet(emission.is_biogenic)) return SCOPES.biogenic;
  return getCategoryScope(emission.category);
};

/** Drops rows without a category or any usable value (same rule the Results view applies). */
export const isUsableEmission = (emission) => {
  if (!emission || typeof emission !== "object" || !emission.category) return false;
  if (String(emission.category).trim().length === 0) return false;
  const gwp = toFiniteNumber(emission.gwp) || toFiniteNumber(emission.gwp_total);
  const biogenic = toFiniteNumber(emission.biogenic_gwp);
  return gwp > 0 || biogenic > 0 || isFlagSet(emission.is_avoided) || isFlagSet(emission.is_biogenic);
};

/**
 * Normalized emission rows: { id, scope, category, description, gwpKg, isTonneBased }.
 */
export const normalizeEmissions = (emissions = [], details = []) => {
  const detailsByEmission = groupDetailsByEmission(details);
  return (emissions || []).filter(isUsableEmission).map((e) => {
    const isTonneBased = isTonneBasedEmission(e, detailsByEmission);
    return {
      id: e.id,
      scope: getEmissionScope(e),
      category: String(e.category).trim(),
      description: e.description || "",
      gwpKg: getRawEmissionGwp(e) * (isTonneBased ? KG_PER_TONNE : 1),
      isTonneBased,
    };
  });
};

const positiveOrNull = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Pollutant masses for one detail row. Priority: stored total, stored factor × activity,
 * then the matching emission_detail_options factor × activity.
 */
export const computeDetailPollutants = (detail, emissionDetailOptions) => {
  const quantity = toFiniteNumber(detail.quantity);
  const factor = detail.factor === null || detail.factor === undefined || detail.factor === "" ? 1 : toFiniteNumber(detail.factor);
  const uncertainty = detail.uncertainty === null || detail.uncertainty === undefined || detail.uncertainty === "" ? 1 : toFiniteNumber(detail.uncertainty);
  const activity = quantity * factor * uncertainty;

  let option = null;
  if (emissionDetailOptions) {
    const matches = findEmissionOptionMatches(emissionDetailOptions, detail.type, detail.category, detail.sub_category, detail.description);
    const unit = normalizeText(detail.unit);
    option = (unit && matches.find((m) => normalizeText(m.unit) === unit)) || matches[0] || null;
  }

  const result = {};
  POLLUTANT_KEYS.forEach((key) => {
    const stored = positiveOrNull(detail[`total_${key}`]);
    const storedFactor = positiveOrNull(detail[`${key}_factor`]);
    const optionFactor = option ? positiveOrNull(option[key]) : null;
    const value = stored ?? (storedFactor !== null ? activity * storedFactor : optionFactor !== null ? activity * optionFactor : null);
    if (value !== null && value > 0) result[key] = value;
  });
  return result;
};

/**
 * Normalized detail rows linked to their normalized emission:
 * { id, emissionId, scope, category, description, quantity, unit, calculationMethod, co2eKg, pollutantsKg }.
 */
export const normalizeEmissionDetails = (details = [], normalizedEmissions = [], emissionDetailOptions) => {
  const byId = new Map(normalizedEmissions.map((e) => [e.id, e]));
  return (details || [])
    .filter((d) => d && byId.has(d.emission_id))
    .map((d) => {
      const parent = byId.get(d.emission_id);
      const scale = isTonneBasedDetail(d) ? KG_PER_TONNE : 1;
      const pollutants = computeDetailPollutants(d, emissionDetailOptions);
      const pollutantsKg = Object.fromEntries(Object.entries(pollutants).map(([k, v]) => [k, v * scale]));
      return {
        id: d.id,
        emissionId: d.emission_id,
        scope: parent.scope,
        category: parent.category,
        description: d.description || parent.description || "",
        quantity: toFiniteNumber(d.quantity),
        unit: d.unit || "",
        calculationMethod: d.calculation_method || "",
        co2eKg: toFiniteNumber(d.totalco2eq) * scale,
        pollutantsKg,
      };
    });
};

export const summarizeScopes = (emissionRows = []) => {
  const totals = { [SCOPES.scope1]: 0, [SCOPES.scope2]: 0, [SCOPES.scope3]: 0, [SCOPES.avoided]: 0, [SCOPES.biogenic]: 0 };
  emissionRows.forEach((row) => {
    if (row.scope in totals) totals[row.scope] += row.gwpKg;
  });
  const gross = GROSS_SCOPES.reduce((sum, s) => sum + totals[s], 0);
  return { ...totals, gross };
};

/** Value of a detail row for a metric: CO₂e or a pollutant key. */
export const detailMetricValue = (detailRow, metric) =>
  metric === CO2E_METRIC ? detailRow.co2eKg : toFiniteNumber(detailRow.pollutantsKg?.[metric]);

const scopeRank = (scope) => {
  const i = SCOPE_ORDER.indexOf(scope);
  return i === -1 ? SCOPE_ORDER.length : i;
};

/** Scope 3 categories start with their GHG Protocol number ("4. Upstream ..."). */
const categoryNumber = (category) => {
  const n = parseInt(category, 10);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
};

const compareScopeThenCategory = (a, b) =>
  scopeRank(a.scope) - scopeRank(b.scope) || categoryNumber(a.category) - categoryNumber(b.category) || b.value - a.value;

/**
 * Totals per scope + category. CO₂e uses emission rows (complete even without details);
 * pollutant metrics use detail rows.
 */
export const aggregateByCategory = (emissionRows = [], detailRows = [], metric = CO2E_METRIC) => {
  const map = new Map();
  const add = (scope, category, value) => {
    if (!value) return;
    const key = `${scope}|${category}`;
    if (!map.has(key)) map.set(key, { scope, category, value: 0 });
    map.get(key).value += value;
  };
  if (metric === CO2E_METRIC) emissionRows.forEach((r) => add(r.scope, r.category, r.gwpKg));
  else detailRows.forEach((r) => add(r.scope, r.category, detailMetricValue(r, metric)));
  return [...map.values()].filter((r) => r.scope !== SCOPES.uncategorized).sort(compareScopeThenCategory);
};

/** Pollutant totals per scope + category, keyed by emission so rows sharing a category never double count. */
export const aggregatePollutantsByCategory = (detailRows = []) => {
  const map = new Map();
  detailRows.forEach((r) => {
    const key = `${r.scope}|${r.category}`;
    if (!map.has(key)) map.set(key, {});
    const totals = map.get(key);
    Object.entries(r.pollutantsKg || {}).forEach(([p, v]) => {
      totals[p] = (totals[p] || 0) + v;
    });
  });
  return map;
};

/**
 * Totals per detail description, largest first, with cumulative share for a Pareto view.
 */
export const aggregateBySource = (detailRows = [], metric = CO2E_METRIC) => {
  const map = new Map();
  detailRows.forEach((r) => {
    const value = detailMetricValue(r, metric);
    if (!value) return;
    const key = `${r.scope}|${r.description}`;
    if (!map.has(key)) map.set(key, { scope: r.scope, category: r.category, description: r.description, value: 0 });
    map.get(key).value += value;
  });
  const rows = [...map.values()].sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
  const total = rows.reduce((s, r) => s + Math.abs(r.value), 0);
  let running = 0;
  return rows.map((r) => {
    running += Math.abs(r.value);
    return { ...r, share: shareOf(r.value, total), cumulativeShare: shareOf(running, total) };
  });
};

/** Scope 3 categories with their reported total; `reported` is false for categories with no rows. */
export const getScope3Coverage = (emissionRows = []) =>
  scope3Categories.map((category) => {
    const value = emissionRows
      .filter((r) => r.scope === SCOPES.scope3 && normalizeText(r.category) === normalizeText(category))
      .reduce((s, r) => s + r.gwpKg, 0);
    const hasRows = emissionRows.some((r) => r.scope === SCOPES.scope3 && normalizeText(r.category) === normalizeText(category));
    return { category, value, reported: hasRows };
  });

export const filterRows = (rows = [], { scope, category } = {}) =>
  rows.filter((r) => (!scope || r.scope === scope) && (!category || r.category === category));
